// RoiInvestorBenefits.tsx
/* eslint-disable @next/next/no-img-element */
import * as React from "react";
import { Box, Paper, Stack, Typography, Grid, Chip } from "@mui/material";
import CtaButton from "../buttonWithArrow"; // or replace with your own CTA button
import Heading from "../heading";
import Image from "next/image";
import DynamicPillButton from "../buttonWithArrow/whatsappButton";
import { openWhatsAppChat } from "../../utils/onWhatsApp";

const MAX_W = 1300;     // section content cap
const IMG_W = 1200;      // max width for the illustration

export default function RoiInvestorBenefits({ data, onClick, tabData, tab, setTab, setVehicle, right = { xs: 0, md: 10, lg: -15, xl: 10 } }) {
    const { title, subtitle, button, image, showSparkle, bgcolor, width, whatsappButton } = data;

    return (
        <Box
            component="section"
            sx={{
                pt: { xs: 8, md: 4 },
                pb: { xs: 4, md: 8 },
                px: { xs: 2, sm: 3 },
                bgcolor: bgcolor ?? "#f7f9f9",
            }}
        >
            {/* Header */}
            <Box sx={{ maxWidth: MAX_W, mx: "auto", mb: { xs: 2, md: 3 } }}>
                <Stack alignItems="center" spacing={1.25}>
                    <Typography
                        className='jura'
                        sx={{
                            fontSize: { xs: 14, md: 20 },
                            color: "text.secondary",
                            letterSpacing: ".06em",
                        }}
                    >
                        {subtitle}
                    </Typography>

                    {/* Anchor box for the heading + sparkle */}
                    <Box sx={{ position: "relative", width: "100%" }}>
                        <Typography
                            component="h2"
                            className='jura'
                            sx={{
                                fontWeight: 600,
                                fontSize: { xs: 14, sm: 24, md: 32 },
                                lineHeight: 1.2,
                                color: "#111",
                                textAlign: "center",
                                px: { xs: 2, md: 0 }, // tiny side padding helps on very small screens
                            }}
                        >
                            {title}
                        </Typography>

                        {/* sparkle image (now positioned relative to this box) */}
                        <Box
                            aria-hidden
                            sx={{
                                position: "absolute",
                                top: { xs: -14, sm: -16, md: -20, lg: -15 },    // md unchanged; better placement on xs/sm
                                right: right ?? { xs: 0, md: 10, lg: -15, xl: 10 }, // md unchanged
                                width: { xs: 34, sm: 46, md: 50 },  // scale for small screens
                                height: { xs: 34, sm: 46, md: 50 },
                                pointerEvents: "none",
                                zIndex: 1,
                            }}
                        >
                            <Image
                                src="/img/investments/Sparkle.svg"
                                alt=""
                                fill
                                style={{ objectFit: "contain" }}
                                priority
                            />
                        </Box>
                    </Box>
                </Stack>
            </Box>

            {/* Toggle */}
            <Stack
                direction="row"
                spacing={1.5}
                justifyContent="center"
                sx={{ mb: { xs: 2, md: 4 } }}
            >
                {
                    tabData?.map((t, index) => (
                        <Chip
                            key={index}
                            clickable
                            color={tab?.value === t?.value ? "primary" : undefined}
                            onClick={() => {
                                setTab(t)
                                setVehicle(t.value)
                            }}
                            label={t?.label}
                            sx={pillSx(tab?.value === t?.value)}
                        />
                    ))
                }
            </Stack>

            {/* Illustration + floating pills */}
            <Box sx={{ maxWidth: MAX_W, mx: "auto" }}>
                <Box
                    sx={{
                        position: "relative",
                        width: "100%",
                        maxWidth: IMG_W,
                        mx: "auto",
                        display: "flex",
                        justifyContent: "center"
                    }}
                >
                    <Box
                        component="img"
                        src={image?.src} // <-- change to your image path
                        alt={image?.alt}
                        loading="lazy"
                        style={{ width: width ?? "100%", height: "auto", display: "block" }}
                    />
                </Box>

                {/* CTA */}
                {
                    button && !whatsappButton &&
                    <Stack alignItems="center" sx={{ mt: { xs: 4, md: 5 } }}>
                        <CtaButton label={button?.text} onClick={() => onClick()} />
                    </Stack>
                }
                {
                    whatsappButton && !button &&
                    <DynamicPillButton
                        label="Send a message"
                        icon="/img/header/whatsapp.svg"
                        iconPosition="end"
                        bg="#25D366"
                        onClick={() => openWhatsAppChat("Collaborate", "business")}
                        size="md"
                        sx={{ height: "47px !important" }}
                    />
                }

                {
                    whatsappButton && button &&
                    <Stack alignItems="center" display="flex" justifyContent="center" gap={2} sx={{ mt: { xs: 4, md: 5 }, flexDirection: { md: "row !important" } }}>
                        <CtaButton label={button?.text} onClick={() => onClick && onClick()} />
                        <DynamicPillButton
                            label="Send a message"
                            icon="/img/header/whatsapp.svg"
                            iconPosition="end"
                            bg="#25D366"
                            onClick={() => openWhatsAppChat("Collaborate", "business")}
                            size="md"
                            sx={{ height: "47px !important" }}
                        />
                    </Stack>
                }

            </Box>
        </Box>
    );
}

/* --------- Helpers --------- */
const pillSx = (active) => ({
    flexShrink: 0,
    fontWeight: 700,
    borderRadius: "999px",
    px: { xs: 1.5, md: 2.5 },
    py: { xs: 1, md: 3 },
    fontSize: { xs: 8, md: 16 },
    background: active
        ? "linear-gradient(180deg, #75c4c5 0%, #0d8196 100%)"
        : "#e0e0e0",
    color: active ? "#fff" : "#000",
    "&:hover": {
        background: active
            ? "linear-gradient(180deg, #4bb6b4 0%, #249f9c 100%)"
            : "#f7f9f9",
    },
    "& .MuiChip-label": { px: 0, fontWeight: 700 },
});
